'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function CancellationPage() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <section className="bg-primary/10 py-12">
        <div className="container mx-auto px-4">
          <Link href="/">
            <Button variant="ghost" className="mb-8 gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-4">Cancellation Policy</h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            At Kawkb, we understand that plans may change. This Cancellation Policy outlines the
            terms under which cancellations, refunds, and related issues are handled for both guests
            and hosts on our platform.
          </p>
        </div>
      </section>

      {/* Cancellation Policy Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold mb-4">For Guests</h2>

            <h3 className="text-xl font-semibold mb-2">Cancellation by Guest:</h3>
            <p>
              Guests can cancel their reservations through the Kawkb platform. The refund amount
              depends on the cancellation policy set by the host for the specific listing.
            </p>

            <h3 className="text-xl font-semibold mb-2 mt-6">Refund Eligibility:</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Partial Refund (Minus 3%):</strong> If the guest cancels at least 5 days
                before the booking start date, they are eligible for a partial refund minus a 3%
                processing fee.
              </li>
              <li>
                <strong>Full Refund:</strong> If the host does not accept the booking, the guest
                will receive a full refund.
              </li>
              <li>
                <strong>No Refund:</strong> If the guest cancels within 5 days of the booking start
                date, no refund will be issued.
              </li>
            </ul>

            <h3 className="text-xl font-semibold mb-2 mt-6">General Notes:</h3>
            <p>
              Refunds may vary depending on the payment method and are subject to the policies of
              the payment provider. Processing may take up to 14 days to complete.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">For Hosts</h2>

            <h3 className="text-xl font-semibold mb-2">Cancellation by Host:</h3>
            <p>
              If a host cancels a reservation, guests are entitled to a full refund. Repeated
              cancellations by a host may result in penalties or listing suspension.
            </p>

            <h3 className="text-xl font-semibold mb-2 mt-6">Booking Modifications:</h3>
            <p>
              Hosts may offer alternative dates or accommodations if a cancellation is unavoidable.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">Disputes and Resolutions</h2>
            <p>
              If a guest or host disagrees with a cancellation decision, they can file a dispute
              through the Kawkb support system within 48 hours of the decision.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">Changes to This Policy</h2>
            <p>
              We reserve the right to update this Cancellation Policy at any time. Please check back
              regularly to stay informed.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">Contact Us</h2>
            <p>
              For any questions or assistance regarding cancellations, please contact our support
              team at{' '}
              <a href="mailto:support@kawkb.com" className="text-blue-600 hover:underline">
                support@kawkb.com
              </a>
              .
            </p>

            <p className="mt-6 font-medium">
              By using Kawkb, you agree to this Cancellation Policy as part of our Terms of Service.
            </p>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-gray-600 text-sm">
                Last updated:{' '}
                {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
